import useEntity from 'entities/hooks/use-entity';
import React, { memo, useState, useEffect, useRef } from 'react';
import { Image, Box } from 'rebass';
import useOnClickOutside from 'use-onclickoutside';
import useSWR from 'swr';
import { findHTMLFragment } from './htmlFinder';
import get from 'lodash/get';
import { usePageContext } from 'collections/book-creation/PageContext';
import { imageUrlToBlob, renderPositionForVGBImage, scalePosition } from 'collections/watch-me-grow/components/ImageEditor/helper';
import ReactAvatarEditor from 'react-avatar-editor';
import { capCanvasDims } from 'collections/book-creation/helper';
import getBestImageVersion from 'utils/image-versions';

const getImageUrl = image => {
  if (image?.type === 'Video')
    return image?.['preview-url']

  const versionedAvatar = get(image, '["image-versions"][5].url', '');

  if (image.tmpUrl)
    return image.tmpUrl;

  if (versionedAvatar) {
    return versionedAvatar;
  }

  return image.tmpAvatar || image['asset-url'] || '';
};

function NewImageLayer({
  photo,
  destinationImageSrcExists,
  onImageReady,
  renderedAsChild,
  tempURL,
  randomBorder,
  isFromBookCover,
  layerId,
  selectedLayer,
  hidePositionCoordinates,
  setSelectedLayer,
  currentPage,
  isFromTFT,
  ...props
}) {
  const { entity: imageSrc } = useEntity(photo || {});
  const { entity: pageLayerToSelect } = useEntity(props?.['event-destination-layer'])

  const { data } = useSWR(imageSrc?.['canvas-image-id'] ? `/v2/media/${imageSrc?.['canvas-image-id']}` : null);
  let updatedMedium = data?.data?.data?.attributes || imageSrc
  const [fileSrc, setFileSrc] = useState(imageSrc?.['asset-url']);
  // const { currentPage } = usePageContext()
  const [newImageObj, setNewImageObj] = useState(null);
  const [zoom, setZoom] = useState(null);
  const editorDims = capCanvasDims(imageSrc?.width, imageSrc?.height);

  const [error, setError] = useState(false);

  const handleChangeImageUrl = async (url) => {
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setNewImageObj(url);
      return;
    }
    try {
      const blob = await imageUrlToBlob(url);
      setNewImageObj(URL.createObjectURL(blob));
    } catch (err) {
      setNewImageObj(url);
    }
  };
  const mediaAssetUrl = getBestImageVersion(imageSrc).url

  useEffect(() => {
    if (mediaAssetUrl) {
      handleChangeImageUrl(mediaAssetUrl)
    }
  }, [mediaAssetUrl]);

  useEffect(() => {
    const imageURL = getImageUrl(updatedMedium)
    setFileSrc(imageURL);
  }, [updatedMedium]);
  const noOverlay = Object.keys(pageLayerToSelect).length === 0
  const showCursor = (props?.kind === "user" || !noOverlay) && props?.onMediaClick && props?.pageId === currentPage?.id
  const isTFTLayer = isFromTFT || props?.['pageName'].includes('TFT');

  const isImageCropped = Object.entries(imageSrc)
    .some(([key, value]) =>
      ["crop-left", "crop-top", "crop-width", "crop-height", "left", "top"].includes(key) &&
      value !== 0
    )
  const getPosition = () => {
    if (props?.isFromPossibilityReview)
      return 'unset'

    if (renderedAsChild)
      return 'relative'

    return 'absolute'
  }

  const onClick = () => {
    if (props?.isFromPossibilityReview) return;

    // Deselect: this layer is already the active one
    if (selectedLayer?.id === layerId) {
      if (setSelectedLayer) setSelectedLayer(null);
      // Clear selectedLayerPageIndex tracked by the parent
      if (props?.onMediaClick) props.onMediaClick(null);
      return;
    }

    // Select: build the layer descriptor
    let e = null;

    if (showCursor) {
      if (Object.keys(pageLayerToSelect).length > 0) {
        e = { ...pageLayerToSelect, photo };
      } else {
        // Include 'source-media' so the parent's Crop Photo button condition
        // (selectedLayer?.['source-media']?.type === 'images') resolves correctly.
        e = { id: layerId, type: 'book-page-layers', 'source-media': props?.['source-media'], photo: props?.['source-media'] };
      }
    }

    if (setSelectedLayer)
      setSelectedLayer((prev) => (e?.id !== prev?.id ? e : null));

    // Always call onMediaClick so selectedLayerPageIndex is updated alongside
    // selectedLayer — without this the Crop Photo button never appears.
    if (props?.onMediaClick) props.onMediaClick(e);
  }
  const pickerRef = useRef(null);

  useOnClickOutside(pickerRef, (event) => {
    if (pickerRef.current && pickerRef.current.contains(event.target)) {
      return;
    }
    if (setSelectedLayer && selectedLayer?.id === layerId) {
      const { found } = findHTMLFragment(event.target, pickerRef.current);

      if (!found && event.target.textContent.trim() !== props?.storyDetails && event.target.textContent.trim() !== "Crop Photo" && event.target.textContent.trim() !== "Edit Text")
        setSelectedLayer(null);
    }
  });

  return (<Box
    onClick={onClick}
    ref={pickerRef}
    id={props?.id}
  >
    {selectedLayer && selectedLayer?.id === layerId && <Box
      width={`${props.width}px`}
      height={`${props.height}px`}
      bg='rgba(125, 194, 255, 0.50)' sx={{
        position: getPosition(),
        cursor: (showCursor || props?.isFromPossibilityReview) ? 'pointer' : 'default',
        left: `${props?.['relative-xpos']}px`,
        top: `${props?.['relative-ypos']}px`,
        zIndex: props['render-order'] + 1,
      }} />}
    <Box
      width={isTFTLayer && isImageCropped ? `${props.width + 40}px` : `${props.width}px`}
      height={isTFTLayer && isImageCropped ? `${props.height + 20}px` : `${props.height}px`}
      sx={{
        position: getPosition(),
        cursor: (showCursor || props?.isFromPossibilityReview) ? 'pointer' : 'default',
        left: isTFTLayer && isImageCropped ? `${props?.['relative-xpos'] - 20}px` : `${props?.['relative-xpos']}px`,
        top: isTFTLayer && isImageCropped ? `${props?.['relative-ypos'] - 20}px` : `${props?.['relative-ypos']}px`,
        zIndex: props['render-order'],
        writingMode: 'horizontal-tb',
        touchAction: 'pan-x pan-y pinch-zoom',
        border: randomBorder,
        position: props?.isFromPossibilityReview ? 'unset' : 'absolute'
      }}
      ref={(el) => {
        if (!el) return;

        const childWidth = imageSrc?.width;
        const childHeight = imageSrc?.height;

        const parentWidth = el.getBoundingClientRect().width;
        const parentHeight = el.getBoundingClientRect().height;

        const updatedZoom = Math.min(
          parentWidth / childWidth,
          parentHeight / childHeight
        ) * 100;

        setZoom(`${updatedZoom}%`); // 54.37%
      }}
    >
      {Object.keys(imageSrc).length === 0 ?
        <Box sx={{
          bg: 'transparent'
        }} />
        : ((props?.pageName?.includes('WMG') || isTFTLayer) && isImageCropped) ? <Box
          sx={isTFTLayer && { borderRadius: '50%' }}
          height={isTFTLayer ? props.height + 20 : props?.height}
          width={isTFTLayer ? props.width + 40 : props?.width}
        >
          <ReactAvatarEditor
            image={newImageObj}
            height={isTFTLayer ? props.height + 20 : editorDims?.height}
            width={isTFTLayer ? props.width + 40 : editorDims?.width}
            color={isTFTLayer ? [250, 250, 250, 1] : [250, 250, 250, 0]}
            scale={isTFTLayer ? imageSrc?.zoom : (+scalePosition(imageSrc?.zoom) * 3) / 100}
            position={renderPositionForVGBImage({
              x: imageSrc?.left,
              y: imageSrc?.top,
            })}
            crossOrigin="anonymous"
            style={{
              minWidth: "100%",
              width: "100%",
              height: "100%",
              objectFit: 'cover',
              borderRadius: isTFTLayer ? '50%' : 'none'
              // zoom,
            }}
          />
        </Box>
          :
          <Image
            src={tempURL || fileSrc}
            id={props?.id}
            sx={{
              objectFit: 'cover',
            }}
            height={props.height}
            width={props.width}
            loading="lazy"
            onError={(e) => {
              const { target } = e;
              setError(true)
              if (target.naturalWidth === 0) {
                console.log('Image not found or failed to load.');
              } else {
                console.log('Error loading image:', e);
              }
            }}
            onLoad={onImageReady}
          />}
    </Box>
  </Box>
  );
}

export default memo(NewImageLayer);